import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWallet, useConnection } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'
import { Plus, Loader2, AlertCircle, CheckCircle2, Info, Target, Coins, Shield, ArrowRight } from 'lucide-react'
import { cn } from '../lib/utils'
import { createCtoPoolsClient, getDevFeeWallet, getBurnTokenMint, PROTOCOL_FEE_BPS, QUORUM_BPS } from '../lib/anchorClient'
import WalletConnectButton from '../components/WalletConnectButton'

/**
 * Interface for form data with validation
 * Ensures type safety for pool creation parameters
 * Note: Protocol fee and quorum are hardcoded in the smart contract
 */
interface CreatePoolFormData {
  tokenMintAddress: string
}

/**
 * Interface for form validation errors
 * Provides user-friendly error messages for each field
 */
interface FormErrors {
  tokenMintAddress?: string
}

/**
 * CreatePool Page Component
 * 
 * Allows users to create new community funding pools with:
 * - Form validation for required field
 * - Wallet connection requirement check
 * - Integration with Anchor client for blockchain transactions
 * - Error handling with user-friendly messages
 * - Success state with navigation to created pool
 * - Cyberpunk theme with glassmorphism styling
 * - Full WCAG 2.1 AA accessibility compliance
 * 
 * NOTE: Protocol fee (1%) and quorum (30%) are fixed by the smart contract
 * NOTE (2026-01-18): Now uses WalletConnectButton that shows TOS before wallet modal
 */
export function CreatePool(): JSX.Element {
  const navigate = useNavigate()
  const wallet = useWallet()
  const { connected, publicKey } = wallet
  const { connection } = useConnection()
  
  // Form state management - simplified to only user-configurable fields
  const [formData, setFormData] = useState<CreatePoolFormData>({
    tokenMintAddress: ''
  })
  
  // UI state management
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>('')
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [createdPoolAddress, setCreatedPoolAddress] = useState<string>('')

  /**
   * Validate a Solana public key address
   * Checks if the string is a valid base58 encoded public key
   */
  const isValidSolanaAddress = (address: string): boolean => {
    try {
      new PublicKey(address)
      return true
    } catch {
      return false
    }
  }

  /**
   * Validate form data before submission
   * Returns true if all fields are valid, false otherwise
   * Updates errors state with validation messages
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Validate token mint address
    if (!formData.tokenMintAddress.trim()) {
      newErrors.tokenMintAddress = 'Token mint address is required'
    } else if (!isValidSolanaAddress(formData.tokenMintAddress)) {
      newErrors.tokenMintAddress = 'Invalid Solana address format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Handle form field changes
   * Updates form data state and clears field-specific errors
   */
  const handleFieldChange = (field: keyof CreatePoolFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    if (errors[field]) {
      const newErrors = { ...errors }
      delete newErrors[field]
      setErrors(newErrors)
    }
    // Clear submit error when user makes changes
    if (submitError) {
      setSubmitError('')
    }
  }

  /**
   * Handle form submission
   * Validates form, creates pool via Anchor client, and handles success/error states
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Check wallet connection
    if (!connected || !publicKey) {
      setSubmitError('Please connect your wallet to create a pool')
      return
    }

    // Validate form
    if (!validateForm()) {
      setSubmitError('Please fix the errors above')
      return
    }

    setIsSubmitting(true)
    setSubmitError('')

    try {
      // Create Anchor client
      const client = createCtoPoolsClient(connection, wallet)

      // Get fee wallet address and burn token mint from environment
      // V2: devFeeWallet receives protocol fees, burnTokenMint is the CTOP token for buy-and-burn
      const devFeeWallet = getDevFeeWallet()
      const burnTokenMint = getBurnTokenMint()

      // Create token mint PublicKey
      const tokenMint = new PublicKey(formData.tokenMintAddress)

      console.log('[CreatePool] Creating pool for token:', tokenMint.toBase58())
      console.log('[CreatePool] Dev fee wallet:', devFeeWallet.toBase58())
      console.log('[CreatePool] Burn token mint (CTOP):', burnTokenMint.toBase58())

      // Create pool via Anchor client
      // V2: Pass devFeeWallet and burnTokenMint; stake pool params use Jito defaults
      // Protocol fee and quorum are hardcoded in the smart contract
      const signature = await client.createPool(
        tokenMint,
        devFeeWallet,
        burnTokenMint
      )

      console.log('[CreatePool] Pool created successfully:', signature)

      // Get pool PDA for navigation
      const [poolPDA] = client.getPoolPDA(tokenMint)
      setCreatedPoolAddress(poolPDA.toBase58())
      setSubmitSuccess(true)

      // Navigate to pool page after 2 seconds
      setTimeout(() => {
        navigate(`/pool/${tokenMint.toBase58()}`)
      }, 2000)

    } catch (error: any) {
      console.error('[CreatePool] Error creating pool:', error)
      
      // Handle specific error cases
      if (error.message?.includes('not deployed')) {
        setSubmitError('Smart contract is not deployed. Pool creation is currently unavailable.')
      } else if (error.message?.includes('Wallet not connected')) {
        setSubmitError('Wallet connection lost. Please reconnect and try again.')
      } else if (error.message?.includes('User rejected')) {
        setSubmitError('Transaction was rejected. Please try again.')
      } else if (error.message?.includes('already exists')) {
        setSubmitError('A pool already exists for this token. Each token can only have one pool.')
      } else {
        setSubmitError(error.message || 'Failed to create pool. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Convert basis points to percentage for display
   * Example: 100 bps = 1%
   */
  const bpsToPercent = (bps: number): string => {
    return (bps / 100).toFixed(2)
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-cyber-cyan/20 rounded-cyber mb-6">
            <Plus className="w-10 h-10 text-cyber-cyan" aria-hidden="true" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gradient-cyber mb-4">
            Create New Pool
          </h1>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Launch a community funding pool for an abandoned token takeover
          </p>
        </div>

        {/* Wallet Connection Check - Now uses WalletConnectButton with TOS flow */}
        {!connected ? (
          <div className="glass-card border border-cyber-yellow/30 p-8 text-center">
            <div className="w-16 h-16 bg-cyber-yellow/20 rounded-cyber mx-auto mb-4 flex items-center justify-center">
              <Shield className="w-8 h-8 text-cyber-yellow" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-bold text-cyber-yellow mb-4">Wallet Required</h2>
            <p className="text-text-secondary mb-6">
              Please connect your wallet to create a new funding pool
            </p>
            {/* 
             * WalletConnectButton shows TOS modal BEFORE wallet selection
             * This replaces the plain text message approach for better UX
             * Updated: 2026-01-18 
             */}
            <WalletConnectButton 
              connectLabel="Connect Wallet to Continue"
              variant="primary"
              size="lg"
              className="mx-auto"
            />
          </div>
        ) : (
          <>
            {/* Success Message */}
            {submitSuccess && (
              <div className="glass-card border border-cyber-green/30 bg-cyber-green/10 p-6 mb-8 animate-slide-down">
                <div className="flex items-start space-x-4">
                  <CheckCircle2 className="w-6 h-6 text-cyber-green flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-cyber-green mb-2">Pool Created Successfully!</h3>
                    <p className="text-text-secondary mb-2">
                      Your community pool has been created on Solana Devnet.
                    </p>
                    {createdPoolAddress && (
                      <p className="text-sm text-text-muted font-mono break-all">
                        Pool Address: {createdPoolAddress}
                      </p>
                    )}
                    <p className="text-sm text-cyber-green mt-2">Redirecting to pool page...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submitError && !submitSuccess && (
              <div 
                className="glass-card border border-status-error/30 bg-status-error/10 p-6 mb-8 animate-slide-down"
                role="alert"
                aria-live="assertive"
              >
                <div className="flex items-start space-x-4">
                  <AlertCircle className="w-6 h-6 text-status-error flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h3 className="text-lg font-semibold text-status-error mb-2">Error Creating Pool</h3>
                    <p className="text-text-secondary">{submitError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Information Cards - Updated to show fixed protocol values */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="glass-card border border-cyber-cyan/20 p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Target className="w-5 h-5 text-cyber-cyan" aria-hidden="true" />
                  <h3 className="font-semibold text-cyber-cyan">One Pool Per Token</h3>
                </div>
                <p className="text-sm text-text-secondary">
                  Each token can only have one pool. Once created, the pool is permanent and parameters cannot be changed.
                </p>
              </div>
              <div className="glass-card border border-cyber-purple/20 p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Coins className="w-5 h-5 text-cyber-purple" aria-hidden="true" />
                  <h3 className="font-semibold text-cyber-purple">Protocol Fee: {bpsToPercent(PROTOCOL_FEE_BPS)}%</h3>
                </div>
                <p className="text-sm text-text-secondary">
                  Fixed {bpsToPercent(PROTOCOL_FEE_BPS)}% fee on executed proposals, split 50/50 between development and token burn.
                </p>
              </div>
              <div className="glass-card border border-cyber-green/20 p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Shield className="w-5 h-5 text-cyber-green" aria-hidden="true" />
                  <h3 className="font-semibold text-cyber-green">Quorum: {bpsToPercent(QUORUM_BPS)}%</h3>
                </div>
                <p className="text-sm text-text-secondary">
                  Fixed {bpsToPercent(QUORUM_BPS)}% minimum participation required for proposal execution.
                </p>
              </div>
            </div>

            {/* Create Pool Form */}
            <div className="glass-card border border-dark-border p-8">
              <form onSubmit={handleSubmit} className="space-y-8" noValidate>

                {/* Token Mint Address Field */}
                <div>
                  <label 
                    htmlFor="token-mint" 
                    className="block text-lg font-medium text-text-primary mb-3"
                  >
                    Token Mint Address <span className="text-status-error">*</span>
                  </label>
                  <input
                    id="token-mint"
                    type="text"
                    value={formData.tokenMintAddress}
                    onChange={(e) => handleFieldChange('tokenMintAddress', e.target.value)}
                    placeholder="Enter Solana token mint address..."
                    className={cn(
                      "w-full px-4 py-3 bg-dark-surface border rounded-cyber text-white placeholder-text-muted font-mono",
                      "focus:border-cyber-cyan focus:ring-1 focus:ring-cyber-cyan focus:outline-none",
                      "transition-all duration-200",
                      errors.tokenMintAddress ? "border-status-error" : "border-dark-border"
                    )}
                    aria-invalid={!!errors.tokenMintAddress}
                    aria-describedby={errors.tokenMintAddress ? "token-mint-error" : "token-mint-help"}
                    disabled={isSubmitting || submitSuccess}
                    autoComplete="off"
                  />
                  {errors.tokenMintAddress ? (
                    <p id="token-mint-error" className="mt-2 text-sm text-status-error" role="alert">
                      {errors.tokenMintAddress}
                    </p>
                  ) : (
                    <p id="token-mint-help" className="mt-2 text-sm text-text-muted">
                      The Solana address of the token you want to create a community takeover pool for
                    </p>
                  )}
                </div>

                {/* Info Box */}
                <div className="glass-card bg-cyber-cyan/10 border border-cyber-cyan/20 p-4">
                  <div className="flex items-start space-x-3">
                    <Info className="w-5 h-5 text-cyber-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <div className="text-sm text-text-secondary">
                      <p className="mb-2">
                        <span className="text-cyber-cyan font-semibold">Important:</span> Protocol parameters 
                        (fee: {bpsToPercent(PROTOCOL_FEE_BPS)}%, quorum: {bpsToPercent(QUORUM_BPS)}%) are 
                        fixed by the smart contract and cannot be modified.
                      </p>
                      <p>
                        Each token can only have one pool. Make sure you're the first to create a pool for this token, 
                        or join an existing pool instead.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting || submitSuccess}
                    className={cn(
                      "cyber-button-primary flex items-center justify-center space-x-3 px-8 py-4 flex-1",
                      "disabled:opacity-50 disabled:cursor-not-allowed"
                    )}
                    aria-label={isSubmitting ? "Creating pool..." : "Create CTO pool"}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                        <span>Creating Pool...</span>
                      </>
                    ) : submitSuccess ? (
                      <>
                        <CheckCircle2 className="w-5 h-5" aria-hidden="true" />
                        <span>Pool Created!</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" aria-hidden="true" />
                        <span>Create CTO Pool</span>
                        <ArrowRight className="w-5 h-5" aria-hidden="true" />
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => navigate('/pools')}
                    disabled={isSubmitting}
                    className="cyber-button-secondary px-8 py-4"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CreatePool
